import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import { TableHeadCell, Order } from './table-types';
import { getComparator } from './table-utils';
import { EnhancedTableToolbar } from './table-toolbar';
import { EnhancedTableHead } from './table-head';
import { Tooltip, IconButton, SvgIconProps } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface TableProps<T> {
    title: string;
    headerCells: TableHeadCell<T>[];
    data: T[];
    dataRecordIdKey: keyof T;
    onRowEditClick?: (record: T) => void;
    onRowDeleteClick?: (record: T) => void;
    onDeleteSelectedClick?: (selectedIds: readonly T[keyof T][]) => void;
    customRowActions?: {
        element?: JSX.Element;
        muiIcon?: React.ReactElement<SvgIconProps>;
        onClick: (record: T) => void;
        tooltip?: string;
        rowValueKey?: keyof T;
        defaultValue: string | boolean | number
    }[];
    showTableCheckboxes?: boolean;
    defaultRowsPerPage?: number;
    rowsPerPageOptions?: number[];
    customActionsHeader?: string;
}

export function EnhancedTable<T extends { [key: string]: any }>(props: TableProps<T>) {
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof T>();
    const [selected, setSelected] = React.useState<readonly T[keyof T][]>([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(props.defaultRowsPerPage ?? 5);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof T) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = props.data.map((n) => n[props.dataRecordIdKey]);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, name: T[keyof T]) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected: readonly T[keyof T][] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeleteSelected = () => {
        if (props.onDeleteSelectedClick) {
            props.onDeleteSelectedClick(selected);
            setSelected([]);
        }
    };

    const isSelected = (name: T[keyof T]) => selected.indexOf(name) !== -1;

    const renderCellValue = (value: any): JSX.Element => {
        if (value instanceof Array || value instanceof Object) {
            return <>{JSON.stringify(value, null, 2)}</>;
        }

        if (typeof value == 'boolean') {
            return value ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />;
        }

        if (value) {
            return <>{value.toString()}</>;
        }

        return <></>;
    };

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - props.data.length) : 0;

    return (
        <>
            <EnhancedTableToolbar title={props.title} numSelected={selected.length} onDeleteSelectedClick={handleDeleteSelected} />
            <TableContainer>
                <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'medium'}>
                    <EnhancedTableHead<T>
                        headCells={props.headerCells}
                        numSelected={selected.length}
                        order={order}
                        orderBy={orderBy}
                        onSelectAllClick={handleSelectAllClick}
                        onRequestSort={handleRequestSort}
                        rowCount={props.data.length}
                        showActions={!!props.onRowEditClick || !!props.onRowDeleteClick || !!props.customRowActions}
                        customActionsHeader={props.customActionsHeader}
                        showTableCheckboxes={props.showTableCheckboxes}
                    />
                    <TableBody>
                        {props.data
                            .slice()
                            .sort(getComparator<T>(order, orderBy))
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => {
                                const isItemSelected = isSelected(row[props.dataRecordIdKey]);

                                return (
                                    <TableRow
                                        hover
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row[props.dataRecordIdKey]?.toString()}
                                        selected={isItemSelected}
                                        style={{
                                            height: 73
                                        }}
                                    >
                                        {!!props.showTableCheckboxes && (
                                            <TableCell
                                                padding="checkbox"
                                                onClick={(event) => handleClick(event, row[props.dataRecordIdKey])}
                                            >
                                                <Checkbox color="primary" checked={isItemSelected} />
                                            </TableCell>
                                        )}
                                        {props.headerCells
                                            .map((h) => h.id)
                                            .map((rowKey) => (
                                                <TableCell align="center" key={`cell-${rowKey.toString()}-${index}`}>
                                                    {renderCellValue(row[rowKey])}
                                                </TableCell>
                                            ))}
                                        {!(!props.onRowEditClick && !props.onRowDeleteClick && !props.customRowActions) && (
                                            <TableCell padding="none" align='center'>
                                                {!!props.onRowEditClick && (
                                                    <Tooltip title="Edit">
                                                        <IconButton onClick={() => props.onRowEditClick!(row)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {!!props.onRowDeleteClick && (
                                                    <Tooltip title="Delete">
                                                        <IconButton onClick={() => props.onRowDeleteClick!(row)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {props.customRowActions?.map((cra, index) => (
                                                    <Tooltip title={cra.tooltip} key={`action-${cra.rowValueKey?.toString()}-${index}`}>
                                                        <>
                                                            {!!cra.element &&
                                                                React.cloneElement(cra.element, {
                                                                    checked: cra.rowValueKey ? (row[cra.rowValueKey] ?? cra.defaultValue) : undefined,
                                                                    value: cra.rowValueKey ? (row[cra.rowValueKey] ?? cra.defaultValue) : undefined,
                                                                    onClick: () => cra.onClick(row)
                                                                })}
                                                            {!!cra.muiIcon && (
                                                                <IconButton onClick={() => cra.onClick(row)}>{cra.muiIcon}</IconButton>
                                                            )}
                                                        </>
                                                    </Tooltip>
                                                ))}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        {emptyRows > 0 && (
                            <TableRow
                                style={{
                                    height: 73 * emptyRows
                                }}
                            >
                                <TableCell colSpan={8} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={props.rowsPerPageOptions ?? [5, 10, 25]}
                component="div"
                count={props.data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </>
    );
}
